
import { useState } from "react";
import SEO from '@/components/SEO';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Hero from "@/components/Hero";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Check, ArrowRight, Code, BarChart, Clock, LineChart, Database, Share2 } from "lucide-react";

const SasMigrations = () => {
  const [openAccordion, setOpenAccordion] = useState<string | null>("item-1");

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="SAS Migrations | Stratodata"
        description="Migrate from legacy SAS to modern cloud data platforms. We handle the full migration — code translation, data validation and team enablement."
        canonical="/services/sas-migrations"
      />
      <Navigation />
      
      {/* Hero Section */}
      <Hero
        title="SAS to Python Migration Services"
        subtitle="Modernize your analytics stack with a proven migration methodology that preserves your insights while unlocking new possibilities."
        ctaText="Contact Us"
        ctaLink="/tell-me-more"
        className="pt-24"
      />
      
      {/* Overview Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Migration Expertise You Can Trust</h2>
              <p className="text-lg text-muted-foreground mb-6">
                SAS has been a leader in the analytics space for decades, but many organizations are now looking to migrate to more flexible, open-source solutions like Python.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                At StratoData, we specialize in helping organizations transition from SAS to Python while preserving the integrity of your analytical processes and business insights.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Check className="h-6 w-6 text-accent mr-3 flex-shrink-0 mt-1" />
                  <p>Proven migration methodology refined across dozens of projects</p>
                </div>
                <div className="flex items-start">
                  <Check className="h-6 w-6 text-accent mr-3 flex-shrink-0 mt-1" />
                  <p>Full transparency with detailed documentation and knowledge transfer</p>
                </div>
                <div className="flex items-start">
                  <Check className="h-6 w-6 text-accent mr-3 flex-shrink-0 mt-1" />
                  <p>Code validation ensuring consistent results between SAS and Python</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-lg shadow-lg p-8 relative z-10">
                <h3 className="text-xl font-semibold mb-4">Why Organizations Choose Python Over SAS</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-1" />
                    <span>Significant cost savings on licensing fees</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-1" />
                    <span>Access to a larger talent pool of Python developers</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-1" />
                    <span>Better integration with modern data science workflows</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-1" />
                    <span>Extensive ecosystem of open-source libraries</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-1" />
                    <span>Flexibility to run anywhere without license constraints</span>
                  </li>
                </ul>
              </div>
              <div className="absolute -top-4 -right-4 w-full h-full bg-primary/20 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Migration Process Section */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Migration Process</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We've developed a comprehensive approach to SAS to Python migrations that minimizes disruption while maximizing the benefits of your transition.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-enhanced relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
              <span className="inline-block bg-primary text-primary-foreground rounded-full px-3 py-1 text-sm font-medium mb-4">Phase 1</span>
              <h3 className="text-xl font-semibold mb-3">Assessment & Planning</h3>
              <p className="text-muted-foreground mb-4">We begin with a thorough analysis of your SAS codebase, data environments, and business requirements.</p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>SAS inventory and complexity assessment</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Python architecture planning</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Migration roadmap development</span>
                </li>
              </ul>
            </div>
            
            <div className="card-enhanced relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
              <span className="inline-block bg-primary text-primary-foreground rounded-full px-3 py-1 text-sm font-medium mb-4">Phase 2</span>
              <h3 className="text-xl font-semibold mb-3">Migration Implementation</h3>
              <p className="text-muted-foreground mb-4">We meticulously convert your SAS code to Python, ensuring functionality and performance are preserved.</p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>SAS procedure to Python library mapping</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Code conversion with parallel validation</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Rigorous testing and quality assurance</span>
                </li>
              </ul>
            </div>
            
            <div className="card-enhanced relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
              <span className="inline-block bg-primary text-primary-foreground rounded-full px-3 py-1 text-sm font-medium mb-4">Phase 3</span>
              <h3 className="text-xl font-semibold mb-3">Deployment & Knowledge Transfer</h3>
              <p className="text-muted-foreground mb-4">We ensure your team is ready to take full advantage of your new Python environment.</p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Production deployment support</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Documentation and training</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                  <span>Ongoing optimization recommendations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Key Considerations Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Key Considerations in SAS to Python Migration</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Based on our experience across numerous migration projects, we've identified critical factors that determine migration success.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <Code className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Code Translation Strategy</h3>
                  <p className="text-muted-foreground">We map SAS procedures to their equivalent Python libraries and functions, ensuring consistency in data handling and analytical results.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <BarChart className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Statistical Consistency</h3>
                  <p className="text-muted-foreground">We ensure numerical results between SAS and Python are consistent, including proper handling of different default behaviors in statistical functions.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <Database className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Data Integration</h3>
                  <p className="text-muted-foreground">Our approach ensures seamless connectivity with your existing data sources, warehouse solutions, and downstream systems.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <Clock className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Performance Optimization</h3>
                  <p className="text-muted-foreground">We optimize Python code to match or exceed SAS performance, particularly for large datasets where processing efficiency is critical.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <LineChart className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Reporting & Visualization</h3>
                  <p className="text-muted-foreground">We replace SAS reporting capabilities with Python's powerful visualization libraries, enhancing your data storytelling capabilities.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-5">
                <div className="bg-primary/20 p-3 rounded-lg">
                  <Share2 className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Governance & Deployment</h3>
                  <p className="text-muted-foreground">Our process includes establishing version control, CI/CD pipelines, and appropriate governance for your Python codebase.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQs Section */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Frequently Asked Questions</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Get answers to common questions about SAS to Python migrations.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion
              type="single"
              collapsible
              value={openAccordion || undefined}
              onValueChange={(value) => setOpenAccordion(value)}
              className="space-y-4"
            >
              <AccordionItem value="item-1" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-medium py-4">
                  How long does a typical SAS to Python migration take?
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  The timeline varies based on your codebase size and complexity. A small project might take 2-3 months, while larger enterprise-wide migrations can span 6-12 months. Our assessment phase provides you with a detailed timeline specific to your environment.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-medium py-4">
                  Will we lose any functionality when moving from SAS to Python?
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  With proper planning, you shouldn't lose any critical functionality. Python's extensive library ecosystem can match or exceed SAS capabilities in most areas. We identify any potential gaps early in the assessment phase and develop strategies to address them.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-medium py-4">
                  How do you ensure the Python code produces the same results as SAS?
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  We employ rigorous validation testing, comparing results between SAS and Python using your actual data. This includes both unit testing of individual components and end-to-end validation of complete analytical processes.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-medium py-4">
                  Do we need to migrate everything at once, or can it be done incrementally?
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  Most organizations benefit from a phased approach. We typically recommend migrating in logical business units or analytical processes, allowing for validation and knowledge transfer before moving to the next phase. This reduces risk and allows your team to adapt gradually.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-medium py-4">
                  What training will our team need?
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  We provide comprehensive knowledge transfer, including documentation of the converted code and training sessions tailored to your team's background. If your team is new to Python, we can include Python fundamentals training as part of the engagement.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/80 to-accent/80 text-primary-foreground">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Modernize Your Analytics Stack?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Take the first step toward a more flexible, cost-effective analytics environment with our SAS to Python migration expertise.
          </p>
          <Link
            to="/book-a-call"
            className="inline-flex items-center bg-white text-accent px-8 py-3 rounded-md shadow-lg text-lg font-medium transition-all hover:bg-white/90 hover:scale-[1.03] active:scale-[0.98]"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SasMigrations;
